import React from 'react';
import { useNavigate } from 'react-router-dom';
import { consumeOverlayEntry } from '../../mobile';
import { Alert, Button, Spinner } from '@parsina/framework';

import { useTranslation } from '../../i18n';
import { C } from '../../constants/theme';
import AdaptiveModal from '../../components/common/AdaptiveModal';
import { STACK } from '../../constants/density.js';
import { useAccess } from '../../hooks/useAccess.js';
import { doctorColor, getInitials } from '../Booking/helpers';
import { InvitePane } from '../People/components/AddPersonBody.jsx';

/**
 * «اتصال پزشک» — what «ثبت نوبت» shows when the clinic has nobody to book for.
 *
 * A booking modal that opens onto «پزشک فعالی وجود ندارد» names the problem and
 * then leaves the receptionist alone with it: the sentence is true, the close
 * button is the only control, and the fix lives two screens away on a page the
 * receptionist may never have opened. This replaces that dead end with the
 * shortest path to a bookable doctor, in the order the clinic is most likely to
 * need it:
 *
 *   1. Links that are already waiting. A doctor who scanned the clinic's code
 *      but has not been approved is the one-click case — «تأیید پیوند» makes
 *      them bookable, and the parent's `bookable` recomputes, so the picker
 *      (or straight the booking modal, when they are the only one) appears in
 *      place without reopening anything.
 *   2. The clinic's invite code and QR, so a doctor standing at the desk can
 *      be connected on the spot. This is the People page's own invite pane,
 *      mounted here rather than copied, so the two never disagree on the link.
 *   3. When the viewer holds neither `doctors.manage` nor an admin tier, both
 *      of those are somebody else's to do — the modal says whose, instead of
 *      offering a button that would fail on submit.
 *
 * The chrome mirrors the doctor picker it stands in for (both are an
 * `AdaptiveModal`, so both are a card on the desk and a bottom sheet on a
 * phone), so the transition from «no doctor» to «pick a doctor» after an accept
 * is a change of content, not of place.
 */
export default function ConnectDoctorModal({ pending = [], onAccept, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can, isAdmin } = useAccess();
  const canManage = isAdmin || can('doctors.manage');

  const [busyId, setBusyId] = React.useState(null);
  const [error, setError] = React.useState('');

  const accept = async (link) => {
    if (busyId || !onAccept) return;
    setError('');
    setBusyId(link.id);
    try {
      await onAccept(link.id);
      // No close here: the parent re-derives `bookable` from the accepted link
      // and swaps this modal for the picker on its own.
    } catch (e) {
      setError(e?.message || t('reception.connectDoctor.acceptFailed'));
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Leave for «همکاران».
   *
   * On a phone this modal is a sheet holding a history entry, so the entry has
   * to be handed to the navigation — otherwise the sheet's deferred release
   * pops the destination straight back off and the button does nothing. On the
   * desk it is a centred dialog with no entry at all, and `consumeOverlayEntry`
   * says so by returning false, which leaves this an ordinary push.
   */
  const goToPeople = () => {
    const replace = consumeOverlayEntry();
    onClose?.();
    navigate('/people?kind=practitioner', { replace });
  };

  const body = (
    <>
      {pending.length > 0 && (
        <section aria-label={t('reception.connectDoctor.pendingTitle')} style={{ display: 'flex', flexDirection: 'column', gap: STACK.tight }}>
          <div style={{ fontSize: '0.8125rem', fontWeight: 800, color: C.textPrimary }}>
            {t('reception.connectDoctor.pendingTitle')}
          </div>
          <div style={{ fontSize: '0.75rem', color: C.textMuted, lineHeight: 1.8 }}>
            {canManage
              ? t('reception.connectDoctor.pendingHint')
              : t('reception.connectDoctor.pendingAwaitingManager')}
          </div>
          {pending.map((link) => {
            const name = link.doctorName || t('doctors.placeholder.unnamed', { id: (link.doctorId || '').slice(0, 6) });
            const busy = busyId === link.id;
            return (
              <div
                key={link.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 12px', borderRadius: 12,
                  border: `1.5px solid ${C.border}`, background: C.bg,
                }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                  background: doctorColor(link.doctorId), color: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.75rem', fontWeight: 800,
                }}>
                  {getInitials(name)}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: 700, color: C.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                  <div style={{ fontSize: '0.71875rem', color: C.amber, marginTop: 2 }}>{t('people.status.pending')}</div>
                </div>
                {canManage && (
                  <Button size="sm" onClick={() => accept(link)} disabled={!!busyId}>
                    {busy ? <Spinner size={14} /> : t('doctors.actions.acceptLink')}
                  </Button>
                )}
              </div>
            );
          })}
          {error && <Alert variant="error">{error}</Alert>}
        </section>
      )}

      {canManage ? (
        <section aria-label={t('reception.connectDoctor.inviteTitle')} style={{ display: 'flex', flexDirection: 'column', gap: STACK.tight }}>
          {pending.length > 0 && (
            <div style={{ fontSize: '0.8125rem', fontWeight: 800, color: C.textPrimary, marginTop: 4 }}>
              {t('reception.connectDoctor.inviteTitle')}
            </div>
          )}
          <InvitePane open />
        </section>
      ) : (
        <Alert variant="info">{t('reception.connectDoctor.needPermission')}</Alert>
      )}
    </>
  );

  const actions = (
    <>
      {canManage && (
        <Button variant="outline" onClick={goToPeople}>{t('reception.connectDoctor.manageColleagues')}</Button>
      )}
      <Button variant="ghost" onClick={onClose}>{t('common.actions.close')}</Button>
    </>
  );

  return (
    <AdaptiveModal
      onClose={onClose}
      title={t('reception.connectDoctor.title')}
      subtitle={t('reception.connectDoctor.subtitle')}
      snap={0.9}
      footer={<div style={{ display: 'flex', gap: 8 }}>{actions}</div>}
      desktop={(
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('reception.connectDoctor.title')}
          style={{
            position: 'fixed', inset: 0, zIndex: 8000, background: 'rgba(15,28,46,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
          <div style={{
            background: 'white', borderRadius: 20, width: '100%', maxWidth: 460,
            maxHeight: '86vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
            boxShadow: '0 32px 80px rgba(0,0,0,0.26)', fontFamily: 'Meshkat, sans-serif',
          }}>
            <div style={{ padding: '18px 22px 14px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '1.0625rem', fontWeight: 800, color: C.textPrimary }}>{t('reception.connectDoctor.title')}</div>
                <div style={{ fontSize: '0.75rem', color: C.textMuted, marginTop: 3, lineHeight: 1.8 }}>{t('reception.connectDoctor.subtitle')}</div>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label={t('common.actions.close')}
                style={{ background: C.bg, border: 'none', cursor: 'pointer', color: C.textMuted, width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px 20px', display: 'flex', flexDirection: 'column', gap: STACK.regular }}>
              {body}
            </div>

            <div style={{ padding: '12px 22px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: 8, justifyContent: 'flex-start' }}>
              {actions}
            </div>
          </div>
        </div>
      )}
    >
      {body}
    </AdaptiveModal>
  );
}
